import type { SupabaseClient } from '@supabase/supabase-js';
import {
  createGrid,
  type ColDef,
  type GridApi,
  type GridOptions,
  type ICellRendererParams,
  type IDatasource,
  type IGetRowsParams,
} from 'ag-grid-community';
import { awaitJob } from '../../lib/await-job.js';
import { openModal, createKfDrawer, createKfToast, KF_ICON, KF_LOGO_GOECOMMERCE } from '@vario-connect/frontend-design';
import { openOttoAssignDialog, applyOfferCategory, applyOfferBrand, type ProvisionResult } from './otto-offers-article-dialog.js';
import { varioArticleUrl, varioListingUrl, varioSalesOrderUrl } from '../../lib/vario-links.js';
import { httpErrorText } from '../../lib/session-expiry.js';

interface OrdersCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

// ---------------------------------------------------------------------------
// Otto-Bestellungen-Fenster (go-eCommerce-Design / kf-page): volle Tabelle +
// 50%-Detail-Drawer. Liste serverseitig paginiert (Infinite-Row-Model); Detail
// (Adressen/Positionen) lazy beim Öffnen des Drawers geladen + gecacht.
// v1-Scope: reine Übersicht + Versand/Storno. KEINE Vario-Übernahme, KEIN
// Artikel-Mapping-Dialog, kein Refund/Beleg (Otto hat dafür kein API-Pendant).
// Otto hat genau EINEN Verkaufskanal ('otto') → kein Storefront-Filter, keine
// Buyer-E-Mail (Kundenname kommt aus der Lieferadresse).
// ---------------------------------------------------------------------------

interface MasterRow {
  kind: 'master';
  storefront: string;
  id_order: string;
  order_number: string | null;
  ts_created: string | null;
  total_price_cents: number | null;
  currency: string | null;
  status_agg: AggStatus;
  /** Lokaler, poll-sicherer Versand-Zustand (aggregiert über die Positionen): 'reported' = an Otto
   *  gemeldet (Versendet), 'reporting' = Meldung läuft (Wird versendet), null = kein Versand. */
  ship_state: 'reported' | 'reporting' | null;
  units_count: number;
  units_mapped_count: number;
  // Anreicherung pro Seite (separate Queries, da die View sie nicht liefert):
  customer_name: string | null;
  tracking: string | null;
  /** Gesetzt = als Vario-Kundenauftrag übernommen (Zeile grün, Status „Übernommen" + Deeplink). */
  vario_order_id: string | null;
  vario_order_number: string | null;
}

type AggStatus = 'announced' | 'processable' | 'sent' | 'returned' | 'cancelled' | 'mixed' | 'unknown';

/** Status-Pill im neuen Design (Punkt + Text). */
const STATUS_AGG_DESIGN: Record<AggStatus, { cls: string; label: string }> = {
  announced:   { cls: 'st-todo',  label: 'Angekündigt' },
  processable: { cls: 'st-todo',  label: 'Zu versenden' },
  mixed:       { cls: 'st-todo',  label: 'Gemischt' },
  sent:        { cls: 'st-ship',  label: 'Versendet' },
  returned:    { cls: 'st-muted', label: 'Retourniert' },
  cancelled:   { cls: 'st-muted', label: 'Storniert' },
  unknown:     { cls: 'st-muted', label: '…' },
};

// Otto-fulfillmentStatus → DB-Werte (uppercase) für den Status-Filter.
const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string; dbStatuses: readonly string[] }> = [
  { value: '',            label: 'Alle Status',  dbStatuses: [] },
  { value: 'announced',   label: 'Angekündigt',  dbStatuses: ['ANNOUNCED'] },
  { value: 'processable', label: 'Zu versenden', dbStatuses: ['PROCESSABLE'] },
  // Lokale, poll-sichere Versand-Zustände (Sonder-Filter, siehe loadOrdersPage): dbStatuses leer,
  // die Einschränkung läuft über otto.order_units.local_ship_state (+ status='SENT' für „Versendet").
  { value: 'shipping',    label: 'Wird versendet', dbStatuses: [] },
  { value: 'sent',        label: 'Versendet',    dbStatuses: [] },
  { value: 'returned',    label: 'Retourniert',  dbStatuses: ['RETURNED'] },
  { value: 'cancelled',   label: 'Storniert',    dbStatuses: ['CANCELLED_BY_PARTNER', 'CANCELLED_BY_MARKETPLACE'] },
  // Sonder-Filter: als Vario-Kundenauftrag übernommen (vario_order_id gesetzt) — kein Fulfillment-
  // Status, daher dbStatuses leer; die Einschränkung passiert in loadOrdersPage über otto.orders.
  { value: 'taken_over',  label: 'Übernommen',   dbStatuses: [] },
];

function dbStatusesForFilter(value: string | null): readonly string[] {
  if (!value) return [];
  return STATUS_FILTER_OPTIONS.find((o) => o.value === value)?.dbStatuses ?? [];
}

function statusGroup(status: string): AggStatus {
  switch (status) {
    case 'ANNOUNCED': return 'announced';
    case 'PROCESSABLE': return 'processable';
    case 'SENT': return 'sent';
    case 'RETURNED': return 'returned';
    case 'CANCELLED_BY_PARTNER':
    case 'CANCELLED_BY_MARKETPLACE': return 'cancelled';
    default: return 'unknown';
  }
}

/** Order-Status = niedrigster Item-Status (Otto-Konvention); gemischt, wenn uneinheitlich. */
function aggregateStatus(statuses: Array<string | null>): AggStatus {
  const groups = statuses.filter((s): s is string => !!s).map(statusGroup);
  if (groups.length === 0) return 'unknown';
  const set = new Set(groups);
  if (set.size === 1) return groups[0]!;
  if (set.has('announced')) return 'announced';
  if (set.has('processable')) return 'processable';
  return 'mixed';
}

interface DetailRow {
  kind: 'detail';
  storefront: string;
  id_order: string;
  id_order_unit: string;
  sku: string | null;
  ean: string | null;
  article_number: string | null;
  title: string | null;
  status: string | null;
  quantity: number | null;
  unit_price_cents: number | null;
  currency: string | null;
  carrier_code: string | null;
  tracking_number: string | null;
  cancellation_reason: string | null;
  ts_status_changed: string | null;
  vario_article_id: string | null;
  vario_article_number: string | null;
  /** Kanal-Listing des gematchten Artikels (View 0301) für den SKU-Deeplink „direkt in den
   *  Verkaufskanal". Fehlt (noch nicht provisioniert) → Fallback auf das Artikel-Dashboard. */
  channel_vario_article_id: string | null;
  channel_vario_listing_id: string | null;
}

/** Otto-Adress-Shape (deliveryAddress / invoiceAddress) — siehe otto-phase0-api.md. */
interface OttoAddress {
  salutation?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  addition?: string | null;
  zipCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
}

interface OrderHeaderFull {
  billing_address: OttoAddress | null;
  shipping_address: OttoAddress | null;
  currency: string | null;
  /** Übernahme-Stand (Migration 0304): gesetzt = bereits als Vario-Kundenauftrag übernommen. */
  vario_order_id: string | null;
  vario_order_number: string | null;
}

interface FilterState {
  status: string | null;
  search: string;
}

const GRID_BLOCK_SIZE = 200;
const GRID_CACHE_BLOCKS = 10;
const STOREFRONT = 'otto';

// Otto-Carrier (POST /v1/shipments, carrier-Feld) — siehe otto-phase0-api.md.
const CARRIERS: Array<{ value: string; label: string }> = [
  { value: 'DHL', label: 'DHL' },
  { value: 'HERMES', label: 'Hermes' },
  { value: 'DPD', label: 'DPD' },
  { value: 'GLS', label: 'GLS' },
  { value: 'UPS', label: 'UPS' },
  { value: 'OTHER_FORWARDER', label: 'Spedition (sonstige)' },
];

// Retouren-Carrier: OTTO nimmt im returnTrackingKey nur diese drei (Shipments V1).
const RETURN_CARRIERS: Array<{ value: string; label: string }> = [
  { value: 'DHL', label: 'DHL' },
  { value: 'GLS', label: 'GLS' },
  { value: 'HERMES', label: 'Hermes' },
];

const orderKey = (m: { storefront: string; id_order: string }) => `${m.storefront}::${m.id_order}`;

interface OrdersSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/** Standalone-Slot-Renderer für den Vario-Verkaufskanal-Tab `/#/orders`
 *  (settings.sales-channels.{appId}.tabs). Rendert die Bestellübersicht über die
 *  volle iframe-Höhe. */
export function renderOrders(ctx: OrdersSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = '';
  void renderOrdersTab(ctx.contentEl, { supabase: ctx.supabase, session: ctx.session, supabaseUrl: ctx.supabaseUrl }, { standalone: true });
}

export async function renderOrdersTab(host: HTMLElement, ctx: OrdersCtx, opts: { standalone?: boolean } = {}) {
  const standalone = !!opts.standalone;
  const appheadHtml = standalone
    ? `<header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Otto · Bestellungen</div><h1>Bestellübersicht</h1></div>
        <div class="hactions">
          <span class="hstatus"><span class="d"></span>Otto verbunden</span>
          <button type="button" class="btn btn-primary" id="kl-orders-refresh" title="Bestellungen jetzt mit Otto synchronisieren">${KF_ICON.refresh}Aktualisieren</button>
        </div>
      </header>`
    : '';
  const refreshInFilterbar = standalone
    ? ''
    : `<button type="button" class="btn btn-primary" id="kl-orders-refresh" title="Bestellungen jetzt mit Otto synchronisieren">${KF_ICON.refresh}Aktualisieren</button>`;
  const filterbar = `<div class="filterbar">
      <div class="select"><select id="kl-orders-status" title="Status filtern">${STATUS_FILTER_OPTIONS.map((o) => `<option value="${o.value}">${o.label}</option>`).join('')}</select></div>
      <div class="tbsearch">${KF_ICON.search}<input id="kl-orders-search" type="search" placeholder="Order-ID, Bestellnummer suchen…" /><button type="button" class="clearbtn" id="kl-orders-search-clear" aria-label="Suche leeren">${KF_ICON.close}</button></div>
      ${refreshInFilterbar}
      <button type="button" class="icon-btn" id="kl-orders-help" title="Hilfe">${KF_ICON.help}</button>
    </div>`;
  const gridWrapStyle = standalone ? 'flex:1;min-height:0;' : 'height:min(74vh,900px);min-height:480px;flex:none;';
  const card = `<div class="card"><div class="grid-wrap" style="${gridWrapStyle}"><div id="kl-orders-grid" class="ag-theme-vario ag-theme-quartz" style="height:100%;width:100%;"></div></div></div>`;
  host.innerHTML = standalone
    ? `<div class="kf-page" style="height:100%;display:flex;flex-direction:column;"><main class="main">${appheadHtml}${filterbar}${card}</main></div>`
    : `<div class="kf-page" style="height:auto;display:block;">${filterbar}${card}</div>`;

  const root = host.querySelector<HTMLElement>('.kf-page')!;
  if (standalone) {
    const fit = () => { root.style.height = window.innerHeight + 'px'; };
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    fit();
  }
  const toast = createKfToast(root);
  const drawer = createKfDrawer(root);

  const filters: FilterState = { status: null, search: '' };
  const state = {
    selectedKey: null as string | null,
    detailsCache: new Map<string, DetailRow[]>(),
    headerCache: new Map<string, OrderHeaderFull>(),
  };
  let gridApi: GridApi<MasterRow> | null = null;

  const datasource: IDatasource = {
    getRows: async (params: IGetRowsParams) => {
      try {
        const { masters, total } = await loadOrdersPage(ctx, filters, params.startRow, params.endRow);
        params.successCallback(masters, total);
      } catch (e) {
        console.error('[orders] datasource.getRows failed', e);
        params.failCallback();
      }
    },
  };

  gridApi = initGrid(host, datasource);
  bindFilters();
  bindRefreshButton();
  host.querySelector<HTMLButtonElement>('#kl-orders-help')?.addEventListener('click', () =>
    toast('Zeile anklicken öffnet die Bestelldetails. Icons rechts: Versand / Storno.'),
  );

  function resetGridForFilterChange() {
    drawer.close();
    state.selectedKey = null;
    state.detailsCache.clear();
    state.headerCache.clear();
    setTimeout(() => {
      gridApi?.paginationGoToFirstPage();
      gridApi?.purgeInfiniteCache();
    }, 0);
  }

  /** Frische Master-Row laden, Grid neu fetchen; wenn ihr Drawer offen ist, neu rendern. */
  async function refreshAfterMutation(storefront: string, idOrder: string) {
    state.detailsCache.delete(idOrder);
    state.headerCache.delete(idOrder);
    setTimeout(() => gridApi?.purgeInfiniteCache(), 0);
    const fresh = await loadSingleMaster(ctx, storefront, idOrder);
    if (fresh && state.selectedKey === orderKey(fresh)) {
      await openOrderDrawer(fresh);
    }
  }

  function bindFilters() {
    const search = host.querySelector<HTMLInputElement>('#kl-orders-search')!;
    const clearBtn = host.querySelector<HTMLButtonElement>('#kl-orders-search-clear')!;
    const updateClearVisibility = () => clearBtn.classList.toggle('show', search.value.length > 0);
    updateClearVisibility();
    let debounce: number | null = null;
    search.addEventListener('input', () => {
      updateClearVisibility();
      if (debounce !== null) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        filters.search = search.value;
        resetGridForFilterChange();
      }, 300) as unknown as number;
    });
    clearBtn.addEventListener('click', () => {
      search.value = '';
      updateClearVisibility();
      filters.search = '';
      resetGridForFilterChange();
      search.focus();
    });
    host.querySelector<HTMLSelectElement>('#kl-orders-status')!.addEventListener('change', (e) => {
      filters.status = (e.target as HTMLSelectElement).value || null;
      resetGridForFilterChange();
    });
  }

  function bindRefreshButton() {
    const btn = host.querySelector<HTMLButtonElement>('#kl-orders-refresh');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try {
        const { data, error } = await ctx.supabase.rpc('otto_enqueue_orders_refresh');
        if (error) throw new Error(error.message);
        const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
        if (jobId) await awaitJob(ctx.supabase, jobId, 180_000);
        resetGridForFilterChange();
        toast('Bestellungen aktualisiert');
      } catch (e) {
        console.error('[orders] manual sync failed', e);
        toast(`Sync fehlgeschlagen: ${(e as Error).message}`);
      } finally {
        btn.disabled = false;
      }
    });
  }

  function initGrid(host: HTMLElement, datasource: IDatasource): GridApi<MasterRow> {
    const eGrid = host.querySelector<HTMLDivElement>('#kl-orders-grid')!;
    const cols: ColDef<MasterRow>[] = [
      { headerName: 'Datum', width: 142, field: 'ts_created',
        cellRenderer: (p: ICellRendererParams<MasterRow>) =>
          el(`<span class="c-date">${escapeHtml(formatDateShort(p.data?.ts_created ?? null))}<span style="color:var(--muted)">${p.data?.ts_created ? ', ' + escapeHtml(timeShort(p.data.ts_created)) : ''}</span></span>`) },
      { headerName: 'Order-Nr.', width: 130, field: 'order_number',
        cellRenderer: (p: ICellRendererParams<MasterRow>) => el(`<span class="c-oid">${escapeHtml(p.data?.order_number ?? p.data?.id_order ?? '')}</span>`) },
      { headerName: 'Kunde', flex: 1.4, minWidth: 150, field: 'customer_name',
        cellRenderer: (p: ICellRendererParams<MasterRow>) =>
          el(`<span class="c-kunde">${escapeHtml(p.data?.customer_name || '—')}</span>`) },
      { headerName: 'Status', width: 168, colId: 'status',
        cellRenderer: (p: ICellRendererParams<MasterRow>) => el(orderStatusCell(p.data)) },
      { headerName: 'Gesamtpreis', width: 130, field: 'total_price_cents', cellClass: 'r', headerClass: 'hr',
        cellRenderer: (p: ICellRendererParams<MasterRow>) =>
          el(`<span class="c-eur">${escapeHtml(formatCents(p.data?.total_price_cents ?? null, p.data?.currency ?? 'EUR'))}</span>`) },
      { headerName: 'Zuordnung', width: 116, colId: 'mapping',
        cellRenderer: (p: ICellRendererParams<MasterRow>) => el(mappingPill(p.data)) },
      { headerName: 'Tracking-Nr.', flex: 1, minWidth: 130, colId: 'tracking',
        cellRenderer: (p: ICellRendererParams<MasterRow>) =>
          el(p.data?.tracking ? `<span class="c-track" title="${escapeHtml(p.data.tracking)}">${escapeHtml(p.data.tracking)}</span>` : `<span class="c-track none">—</span>`) },
      { headerName: 'Aktionen', width: 96, colId: 'actions', pinned: 'right', sortable: false, resizable: false,
        cellStyle: { padding: '0' },
        cellRenderer: (p: ICellRendererParams<MasterRow>) => orderActionsCell(p.data) },
    ];

    const options: GridOptions<MasterRow> = {
      columnDefs: cols,
      defaultColDef: { sortable: false, filter: false, resizable: true, suppressHeaderMenuButton: true },
      rowModelType: 'infinite',
      cacheBlockSize: GRID_BLOCK_SIZE,
      maxBlocksInCache: GRID_CACHE_BLOCKS,
      infiniteInitialRowCount: 50,
      blockLoadDebounceMillis: 100,
      datasource,
      rowHeight: 44,
      headerHeight: 46,
      pagination: true,
      paginationPageSize: 50,
      paginationPageSizeSelector: [50, 100, 200],
      suppressCellFocus: true,
      getRowId: (p) => (p.data ? orderKey(p.data) : ''),
      rowClassRules: {
        'row-sel': (p) => p.data != null && state.selectedKey === orderKey(p.data),
        'row-takeover': (p) => p.data != null && !!p.data.vario_order_id,
      },
      onCellClicked: (e) => {
        if (!e.data) return;
        const target = e.event?.target as HTMLElement | null;
        const actBtn = target?.closest<HTMLElement>('[data-act]');
        if (actBtn) {
          if (actBtn.classList.contains('is-disabled')) return;
          void triggerOrderAction(actBtn.dataset.act as 'ship' | 'cancel', e.data, ctx, toast, () =>
            void refreshAfterMutation(e.data!.storefront, e.data!.id_order),
          );
          return;
        }
        // Klick auf den „Übernommen"-Deeplink öffnet Vario (neuer Tab), NICHT den Drawer.
        if (target?.closest('a.otto-order-link')) return;
        void openOrderDrawer(e.data);
      },
      onPaginationChanged: (ev) => {
        if (!(ev.newPage || ev.newPageSize)) return;
        if (!state.selectedKey) return;
        drawer.close();
        state.selectedKey = null;
      },
    };
    return createGrid(eGrid, options);
  }

  // -------------------------------------------------------------------------
  // Detail-Drawer (50%) — Adressen / Gesamtpreis / Positionen
  // -------------------------------------------------------------------------
  async function openOrderDrawer(order: MasterRow) {
    state.selectedKey = orderKey(order);
    gridApi?.redrawRows();

    let units: DetailRow[] = state.detailsCache.get(order.id_order) ?? [];
    let header: OrderHeaderFull | null = state.headerCache.get(order.id_order) ?? null;
    const needLoad = !state.detailsCache.has(order.id_order) || !state.headerCache.has(order.id_order);
    if (needLoad) {
      try {
        [units, header] = await Promise.all([
          loadOrderUnits(ctx, order.storefront, order.id_order),
          loadOrderHeader(ctx, order.storefront, order.id_order),
        ]);
        state.detailsCache.set(order.id_order, units);
        state.headerCache.set(order.id_order, header);
      } catch (e) {
        toast(`Bestelldetails konnten nicht geladen werden: ${(e as Error).message}`);
        return;
      }
    }
    if (state.selectedKey !== orderKey(order)) return;

    // Sind die 4 Otto-Pflichtmodalitäten gesetzt? Steuert, ob der Übernehmen-Button aktiv ist
    // (frisch bei jedem Öffnen geladen, da die Settings in einem anderen Tab geändert werden können).
    const settingsComplete = await loadOrderTakeoverSettingsComplete(ctx);
    if (state.selectedKey !== orderKey(order)) return;

    // Historie immer frisch laden (nicht gecacht) — spiegelt Übernahme/Versand/Storno sofort.
    let history: OrderHistoryRow[] = [];
    try { history = await loadOrderHistory(ctx, order.id_order); } catch { /* best-effort, Drawer ohne Historie */ }
    if (state.selectedKey !== orderKey(order)) return;

    drawer.open({
      wide: true,
      html: buildOrderDrawerHtml(order, header, units, settingsComplete, history),
      onMount: (content) => {
        // Matching aus der Bestellung heraus — GLEICHER Dialog wie in der Angebotsverwaltung
        // (openOttoAssignDialog, per SKU auf vario.article_mappings). Nach Erfolg Cache leeren
        // und den Drawer neu laden → die Position(en) mit dieser SKU lösen dann auf.
        content.querySelectorAll<HTMLButtonElement>('[data-match-unit]').forEach((btn) => {
          const u = units.find((x) => x.id_order_unit === btn.dataset['matchUnit']);
          if (!u || !u.sku) return;
          const sku = u.sku;
          btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            openOttoAssignDialog({
              supabase: ctx.supabase,
              supabaseUrl: ctx.supabaseUrl,
              session: { token: ctx.session.token },
              unit: {
                sku,
                ean: u.ean,
                title: u.title,
                varioArticleId: u.vario_article_id,
                varioArticleNumber: u.vario_article_number,
              },
              // Kanal-Anlage macht onDone selbst (mit dem Bestellpreis vorher) → im Dialog aus.
              provisionChannel: false,
              onDone: (info) => {
                // Nach der Zuordnung: Otto-Verkaufskanal am Artikel anlegen + EAN & Preis in den
                // Kanal schreiben. Preis ZUERST (otto-set-channel-price — noch kein Lister-Listing
                // → kein verfrühter Otto-Push), dann otto-order-provision-channel (Kanal + EAN +
                // Lister-Zeile). Best-effort; danach Drawer neu laden. Bei „Zuordnung aufheben"
                // liefert provision skipped:not_mapped → keine Erfolgsmeldung.
                void (async () => {
                  const priceEur = typeof u.unit_price_cents === 'number' ? u.unit_price_cents / 100 : null;
                  const hdrs = { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' };
                  const fnBase = `${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1`;
                  try {
                    if (priceEur && priceEur > 0) {
                      await fetch(`${fnBase}/otto-set-channel-price`, { method: 'POST', headers: hdrs, body: JSON.stringify({ sku, priceEur, source: 'assign' }) }).catch(() => null);
                    }
                    const res = await fetch(`${fnBase}/otto-order-provision-channel`, { method: 'POST', headers: hdrs, body: JSON.stringify({ sku, ean: u.ean ?? undefined }) });
                    const j = (await res.json().catch(() => ({}))) as ProvisionResult;
                    // OTTO-Kategorie übernehmen (Einstellung bzw. Antwort im Dialog), nur wenn der
                    // Kanal am Artikel noch keine hat.
                    if (res.ok && !j?.error && !j?.skipped && info?.category) {
                      const c = await applyOfferCategory({
                        supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, token: ctx.session.token,
                        onStatus: (msg) => toast(msg),
                      }, j, info.category);
                      if (c.status === 'error') toast(c.message);
                      else if (c.status === 'set' && c.groupAdded) toast(`Kategoriegruppe „${info.category.group}“ in VARIO übernommen.`);
                    }
                    // OTTO-Marke übernehmen, nur wenn die Lister-Zeile noch keine hat.
                    if (res.ok && !j?.error && !j?.skipped && info?.brand) {
                      const b = await applyOfferBrand(ctx.supabase, j, info.brand);
                      if (b.status === 'error') toast(b.message);
                      else if (b.status === 'set' && b.brandAdded) toast(`Marke „${info.brand.name}“ zu den präferierten Marken hinzugefügt.`);
                    }
                    if (j?.skipped === 'not_mapped') { /* Unassign → nichts provisionieren */ }
                    else if (!res.ok || j?.error) toast(`Kanal-Anlage fehlgeschlagen: ${j?.error?.message ?? httpErrorText(res.status)}`);
                    else if (j?.eanWritten && j.eanWritten.ok === false) toast(`Verkaufskanal + Preis angelegt; EAN nicht gesetzt: ${j.eanWritten.message ?? ''}`);
                    else if (j?.eanWritten && j.eanWritten.ok && j.eanWritten.message && j.eanWritten.message !== 'gesetzt') toast(`Zugeordnet: Verkaufskanal angelegt. EAN ${j.eanWritten.message}.`);
                    else toast('Zugeordnet: Verkaufskanal angelegt, EAN & Preis in den Kanal geschrieben.');
                  } catch (e) {
                    toast(`Kanal-Anlage fehlgeschlagen: ${(e as Error).message}`);
                  }
                  state.detailsCache.delete(order.id_order);
                  state.headerCache.delete(order.id_order);
                  void openOrderDrawer(order);
                })();
              },
            });
          });
        });

        // Manuelle Auftragsübernahme: enqueued vario.order_create_from_otto (Pre-Checks in der RPC),
        // wartet best-effort auf den Job und lädt den Drawer neu (zeigt dann den Vario-Auftrag-Link).
        const takeoverBtn = content.querySelector<HTMLButtonElement>('[data-otto-takeover]');
        if (takeoverBtn && !takeoverBtn.disabled) {
          takeoverBtn.addEventListener('click', () => {
            void (async () => {
              const statusEl = content.querySelector<HTMLElement>('#otto-takeover-status');
              // Spinner im Button (Übernahme dauert etwas: Skript-Lauf + Vario-Auftrag).
              const btnHtml = takeoverBtn.innerHTML;
              takeoverBtn.disabled = true;
              takeoverBtn.innerHTML = '<span class="v-icon fa-light fa-spinner-third fa-spin"></span> Wird übernommen …';
              if (statusEl) statusEl.textContent = 'Übernahme wird gestartet …';
              try {
                const { data: jobId, error } = await ctx.supabase.rpc('otto_enqueue_order_create', {
                  _storefront: order.storefront, _id_order: order.id_order,
                });
                if (error) throw new Error(error.message);
                if (statusEl) statusEl.textContent = 'Übernahme läuft, Vario-Auftrag wird angelegt …';
                if (typeof jobId === 'string') {
                  try { await awaitJob(ctx.supabase, jobId, 60_000); } catch { /* best-effort */ }
                }
                toast('Bestellung übernommen, Vario-Auftrag angelegt.');
                // Grid-Zeile + Drawer frisch laden → Zeile wird grün, Status „Übernommen" + Deeplink.
                // (Der Drawer wird dabei neu gerendert → der Button ist danach ohnehin ersetzt.)
                await refreshAfterMutation(order.storefront, order.id_order);
              } catch (e) {
                if (statusEl) statusEl.textContent = `Übernahme fehlgeschlagen: ${(e as Error).message}`;
                takeoverBtn.innerHTML = btnHtml;
                takeoverBtn.disabled = false;
              }
            })();
          });
        }
      },
      onClose: () => {
        if (state.selectedKey === orderKey(order)) {
          state.selectedKey = null;
          gridApi?.redrawRows();
        }
      },
    });
  }
}

// ---------------------------------------------------------------------------
// Cell-Renderer + Drawer-HTML
// ---------------------------------------------------------------------------

function el(html: string): HTMLElement {
  const e = document.createElement('span');
  e.style.cssText = 'display:inline-flex;align-items:center;max-width:100%;min-width:0;';
  e.innerHTML = html;
  return e;
}

function statusPill(agg: AggStatus): string {
  const info = STATUS_AGG_DESIGN[agg] ?? STATUS_AGG_DESIGN.unknown;
  return `<span class="st ${info.cls}"><span class="pd"></span>${info.label}</span>`;
}

/** Effektiver Anzeige-Status einer Bestellung (Label + Pillen-Klasse), Vorrang von oben:
 *  Versendet (poll-sicher via ship_state) → Wird versendet → Storniert → Übernommen → Fulfillment.
 *  Eine Quelle für Listenzelle UND Drawer-Badge, damit beide identisch sind. */
function effectiveOrderStatus(d: MasterRow | undefined): { label: string; cls: string } {
  if (d?.ship_state === 'reported') return { label: 'Versendet', cls: 'st-ship' };
  if (d?.ship_state === 'reporting') return { label: 'Wird versendet', cls: 'st-todo' };
  if (d?.status_agg === 'cancelled') return { label: 'Storniert', cls: 'st-muted' };
  if (d?.vario_order_id) return { label: 'Übernommen', cls: 'st-done' };
  const info = STATUS_AGG_DESIGN[d?.status_agg ?? 'unknown'] ?? STATUS_AGG_DESIGN.unknown;
  return { label: info.label, cls: info.cls };
}

/** Status-Zelle in der Liste: effektiver Status; bei übernommenen Bestellungen als Deeplink auf den
 *  Vario-Auftrag. */
function orderStatusCell(d: MasterRow | undefined): string {
  const eff = effectiveOrderStatus(d);
  if (d?.vario_order_id) {
    const url = varioSalesOrderUrl(d.vario_order_id);
    const num = d.vario_order_number ?? '';
    if (url) {
      return `<a class="st ${eff.cls} otto-order-link" href="${escapeHtml(url)}" target="_blank" rel="noopener" title="Vario-Auftrag ${escapeHtml(num)} öffnen"><span class="pd"></span>${escapeHtml(eff.label)}</a>`;
    }
  }
  return `<span class="st ${eff.cls}"><span class="pd"></span>${escapeHtml(eff.label)}</span>`;
}

/** Info-Pille: wie viele Positionen sind einem Vario-Artikel zugeordnet. */
function mappingPill(d: MasterRow | undefined): string {
  if (!d || d.units_count === 0) return `<span class="c-doc none">—</span>`;
  const full = d.units_mapped_count >= d.units_count;
  const cls = full ? 'st st-done' : 'st st-muted';
  return `<span class="${cls}" title="In Vario zugeordnete Positionen"><span class="pd"></span>${d.units_mapped_count}/${d.units_count}</span>`;
}

// Symbol-Icons für die Zuordnen-Spalte (platzsparend, nur Icon). Link = noch nicht zugeordnet,
// Stift = zugeordnet (bearbeiten). Der zugeordnete Vario-Artikel steht im Tooltip.
const MATCH_LINK_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 17H7A5 5 0 0 1 7 7h2M15 7h2a5 5 0 0 1 0 10h-2M8 12h8"/></svg>';
const MATCH_PEN_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>';

/** Zuordnen-Zelle im Bestelldetail: platzsparendes Symbol. Klick öffnet den Angebots-Matching-
 *  Dialog (per SKU). Ohne SKU nicht zuordenbar (→ „—"). Zugeordneter Artikel = Tooltip.
 *  Der Klick wird in openOrderDrawer (onMount) über `data-match-unit` verdrahtet. */
function varioMatchCell(u: DetailRow): string {
  if (!u.sku) return `<span class="vart none" title="Keine SKU, nicht zuordenbar">—</span>`;
  const uid = escapeHtml(u.id_order_unit);
  if (u.vario_article_id) {
    const num = u.vario_article_number ?? u.vario_article_id;
    return `<button type="button" class="assign is-mapped" data-match-unit="${uid}" title="Zugeordnet: ${escapeHtml(num)} (bearbeiten)" aria-label="Zuordnung bearbeiten">${MATCH_PEN_ICON}</button>`;
  }
  return `<button type="button" class="assign" data-match-unit="${uid}" title="Vario-Artikel zuordnen" aria-label="Vario-Artikel zuordnen">${MATCH_LINK_ICON}</button>`;
}

/** SKU-Zelle im Bestelldetail. Bei zugeordnetem Artikel wird die SKU zum Deeplink „direkt in den
 *  Verkaufskanal" (Kanal-Listing, View 0301); fehlt die Kanal-Listing-ID → Artikel-Dashboard;
 *  liegen wir nicht in der Vario-iframe → kein Host → nur Text. Ohne SKU → „—". */
function skuCell(u: DetailRow): string {
  if (!u.sku) return '—';
  const sku = escapeHtml(u.sku);
  const url = u.vario_article_id
    ? (varioListingUrl(u.channel_vario_article_id, u.channel_vario_listing_id) ?? varioArticleUrl(u.vario_article_id))
    : null;
  if (!url) return sku;
  return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener" class="sku-link" title="Im Vario-Verkaufskanal öffnen">${sku}</a>`;
}

function orderActionsCell(d: MasterRow | undefined): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'act-cell';
  wrap.style.cssText = 'display:inline-flex;gap:2px;justify-content:center;align-items:center;width:100%;height:100%;';
  if (!d) return wrap;
  const s = d.status_agg;
  const canShip = s === 'processable' || s === 'mixed';
  const canCancel = s === 'announced' || s === 'processable' || s === 'mixed';
  wrap.innerHTML =
    actBtn('ship', KF_ICON.truck, 'Versand anlegen', canShip, '') +
    actBtn('cancel', KF_ICON.ban, 'Stornieren', canCancel, 'danger');
  return wrap;
}

function actBtn(act: string, icon: string, title: string, enabled: boolean, extra: string): string {
  const cls = `act-icon${extra ? ' ' + extra : ''}${enabled ? '' : ' is-disabled'}`;
  return `<button type="button" class="${cls}" data-act="${act}" title="${escapeHtml(title)}"${enabled ? '' : ' disabled'}>${icon}</button>`;
}

const COUNTRY_NAMES: Record<string, string> = {
  DEU: 'Deutschland', AUT: 'Österreich', CHE: 'Schweiz', FRA: 'Frankreich',
  ITA: 'Italien', NLD: 'Niederlande', BEL: 'Belgien', LUX: 'Luxemburg',
  POL: 'Polen', CZE: 'Tschechien', DNK: 'Dänemark', ESP: 'Spanien',
  // 2-Letter-Fallbacks (falls Otto doch ISO-2 liefert):
  DE: 'Deutschland', AT: 'Österreich', CH: 'Schweiz',
};
function countryLabel(code: string | null | undefined): string {
  if (!code) return '';
  const upper = code.toUpperCase();
  return COUNTRY_NAMES[upper] ?? upper;
}

function deriveCustomerName(addr: OttoAddress | null): string | null {
  if (!addr) return null;
  const person = [addr.firstName, addr.lastName].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim();
  return person || null;
}

function addressesEqual(a: OttoAddress | null, b: OttoAddress | null): boolean {
  if (!a || !b) return false;
  const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();
  return norm(a.firstName) === norm(b.firstName)
      && norm(a.lastName) === norm(b.lastName)
      && norm(a.street) === norm(b.street)
      && norm(a.houseNumber) === norm(b.houseNumber)
      && norm(a.zipCode) === norm(b.zipCode)
      && norm(a.city) === norm(b.city)
      && norm(a.countryCode) === norm(b.countryCode);
}

function addressHtml(addr: OttoAddress | null): string {
  if (!addr) return `<div class="addr"><div class="identisch">Keine Adressdaten.</div></div>`;
  const name = [addr.firstName, addr.lastName].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim() || '—';
  const street = [addr.street, addr.houseNumber].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim() || '—';
  const city = [addr.zipCode, addr.city].filter((s): s is string => !!s && s.trim().length > 0).join(' ').trim() || '—';
  const country = countryLabel(addr.countryCode);
  return `<div class="addr">
    <div class="nm">${escapeHtml(name)}</div>
    ${addr.addition ? `<div>${escapeHtml(addr.addition)}</div>` : ''}
    <div>${escapeHtml(street)}</div>
    <div>${escapeHtml(city)}${country ? ' · ' + escapeHtml(country) : ''}</div>
  </div>`;
}

export interface OrderHistoryRow {
  event: string;
  status: string | null;
  summary: string | null;
  created_at: string | null;
  /** Job-/Event-Detail (JSON). Beim Versand-Event enthält es die Lieferschein-`documentId`. */
  detail: Record<string, unknown> | null;
}

/** activity_log-Event → Anzeige-Label für die Bestell-Historie. */
const HISTORY_LABELS: Record<string, string> = {
  order_import: 'Übernommen',
  order_shipment: 'Versendet',
  order_ship: 'Versendet',
  order_cancel: 'Storniert',
  order_return: 'Retourniert',
};

/** Lädt die Ereignis-Historie einer Bestellung aus otto.activity_log (ref_id = id_order). */
async function loadOrderHistory(ctx: OrdersCtx, idOrder: string): Promise<OrderHistoryRow[]> {
  const { data, error } = await ctx.supabase
    .schema('otto')
    .from('activity_log')
    .select('event, status, summary, created_at, detail')
    .eq('storefront', 'otto')
    .eq('ref_id', idOrder)
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) {
    console.warn('[orders] history load failed', error.message);
    return [];
  }
  return (data ?? []) as OrderHistoryRow[];
}

/** Aktion-Link (neuer Tab) für eine Historien-Zeile: Übernommen → Vario-Auftrag öffnen,
 *  Versendet → Lieferschein öffnen (Beleg-`documentId` aus dem Versand-Event-Detail). */
function historyActionHtml(order: MasterRow, h: OrderHistoryRow): string {
  if (h.event === 'order_import') {
    const url = order.vario_order_id ? varioSalesOrderUrl(order.vario_order_id) : null;
    return url ? `<a class="sku-link" href="${escapeHtml(url)}" target="_blank" rel="noopener">Auftrag öffnen</a>` : '';
  }
  if (h.event === 'order_shipment' || h.event === 'order_ship') {
    const docId = h.detail && typeof h.detail['documentId'] === 'string' ? (h.detail['documentId'] as string) : null;
    const url = docId ? varioSalesOrderUrl(docId) : null;
    return url ? `<a class="sku-link" href="${escapeHtml(url)}" target="_blank" rel="noopener">Lieferschein</a>` : '';
  }
  return '';
}

/** Historien-Tabelle: „Eingegangen" (aus ts_created) + alle Order-Ereignisse aus dem activity_log,
 *  je Zeile Datum + Ereignis + Aktion (Auftrag/Lieferschein öffnen). Eigene Scroll-Begrenzung, damit
 *  der Drawer im Auto-Grow-iframe nicht über den erreichbaren Bereich hinauswächst. */
function historySectionHtml(order: MasterRow, history: OrderHistoryRow[]): string {
  const rows: string[] = [];
  if (order.ts_created) {
    rows.push(`<tr><td>${escapeHtml(`${formatDateShort(order.ts_created)}, ${timeShort(order.ts_created)}`)}</td><td>Eingegangen</td><td></td></tr>`);
  }
  for (const h of history) {
    const when = h.created_at ? `${formatDateShort(h.created_at)}, ${timeShort(h.created_at)}` : '—';
    const base = HISTORY_LABELS[h.event] ?? h.summary ?? h.event;
    const label = h.status === 'error' ? `${escapeHtml(base)} (Fehler)` : escapeHtml(base);
    rows.push(`<tr><td>${escapeHtml(when)}</td><td>${label}</td><td class="r">${historyActionHtml(order, h)}</td></tr>`);
  }
  if (rows.length === 0) return '';
  return `<div class="dsec">
    <div class="lab">Historie</div>
    <div style="max-height:260px;overflow:auto;">
      <table class="postable"><thead><tr><th>Datum</th><th>Ereignis</th><th class="r">Aktion</th></tr></thead><tbody>${rows.join('')}</tbody></table>
    </div>
  </div>`;
}

function buildOrderDrawerHtml(order: MasterRow, header: OrderHeaderFull | null, units: DetailRow[], settingsComplete: boolean, history: OrderHistoryRow[]): string {
  const currency = header?.currency ?? order.currency ?? 'EUR';
  let goodsCents = 0;
  for (const u of units) {
    if (u.unit_price_cents !== null) goodsCents += u.unit_price_cents * (u.quantity ?? 1);
  }
  const totalCents = order.total_price_cents ?? goodsCents;
  const dateStr = order.ts_created ? `${formatDateShort(order.ts_created)}, ${timeShort(order.ts_created)}` : '';

  const sameAddr = header ? addressesEqual(header.shipping_address, header.billing_address) : false;
  const billingCol = sameAddr
    ? `<div class="identisch">identisch mit Versand</div>`
    : addressHtml(header?.billing_address ?? null);

  const tracking = units.map((u) => u.tracking_number).find((t) => !!t) ?? order.tracking;

  const positions = units.length === 0
    ? `<div class="identisch">Keine Positionen.</div>`
    : `<table class="postable"><thead><tr><th>SKU</th><th>Bezeichnung</th><th class="r">Menge</th><th class="r">Preis</th><th>Status</th><th style="text-align:center;">Zuordnen</th></tr></thead><tbody>
        ${units.map((u) => `<tr>
          <td class="sku mono">${skuCell(u)}</td>
          <td class="art">${escapeHtml(u.title ?? u.sku ?? u.ean ?? '—')}</td>
          <td class="r">${u.quantity ?? 1}</td>
          <td class="r">${escapeHtml(formatCents(u.unit_price_cents, u.currency ?? currency))}</td>
          <td>${statusPill(statusGroup(u.status ?? ''))}</td>
          <td style="text-align:center;">${varioMatchCell(u)}</td>
        </tr>`).join('')}
      </tbody></table>`;

  // Auftragsübernahme: bereits übernommen → Deeplink auf den Vario-Auftrag; sonst „Übernehmen"-Button.
  // Der Button ist NUR aktiv, wenn (a) die 4 Otto-Pflichtmodalitäten gesetzt sind UND (b) alle
  // Positionen zugeordnet sind — sonst ausgegraut mit erklärendem Tooltip (die RPC würde es ohnehin
  // ablehnen). Fehlende Settings haben Vorrang in der Meldung.
  const allMapped = units.length > 0 && units.every((u) => !!u.vario_article_id);
  const canTakeover = settingsComplete && allMapped;
  const takeoverTip = !settingsComplete
    ? 'Bitte zuerst Zahlart, Zahlungsbedingung, Versandart und Lieferbedingung in den Otto-Einstellungen setzen.'
    : !allMapped
      ? 'Erst alle Positionen zuordnen, dann kann übernommen werden.'
      : 'Als Vario-Kundenauftrag übernehmen';
  const takeoverHint = !settingsComplete
    ? 'Otto-Einstellungen unvollständig: Zahlart, Zahlungsbedingung, Versandart und Lieferbedingung setzen (Einstellungen → Auftragsübernahme).'
    : !allMapped
      ? 'Erst alle Positionen zuordnen, dann kann übernommen werden.'
      : '';
  // Übernommene Aufträge: kein eigener „Übernommen"-Block mehr — die Historien-Tabelle zeigt die
  // Übernahme inkl. Deeplink („Auftrag öffnen"). Nur bei noch nicht übernommenen Aufträgen den
  // „Als Vario-Auftrag übernehmen"-Button rendern.
  const takeoverSection = header?.vario_order_id
    ? ''
    : `<div class="dsec">
        <div class="lab">Vario-Auftrag</div>
        <button type="button" class="v-button v-button--primary" data-otto-takeover title="${escapeHtml(takeoverTip)}"${canTakeover ? '' : ' disabled'}>
          <span class="v-icon fa-light fa-file-import"></span> Als Vario-Auftrag übernehmen
        </button>
        <span class="foot-status" id="otto-takeover-status" style="margin-top:8px;display:block;">${escapeHtml(takeoverHint)}</span>
      </div>`;

  return `
    <div class="dhead">
      <span class="dt">Bestelldetails</span>
      <span class="oid">${escapeHtml(order.order_number ?? order.id_order)}</span>
      <span class="time">${escapeHtml(dateStr)}</span>
      <button type="button" class="dclose" data-kf-drawer-close aria-label="Schließen">${KF_ICON.close}</button>
      <span class="vario-badge ${effectiveOrderStatus(order).cls}" style="width:100%;justify-content:flex-start;"><span class="pd"></span>${escapeHtml(effectiveOrderStatus(order).label)}</span>
    </div>
    <div class="dbody">
      <div class="dsec"><div class="dcols">
        <div><div class="lab">Versand an</div>${addressHtml(header?.shipping_address ?? null)}</div>
        <div><div class="lab">Rechnung an</div>${billingCol}</div>
      </div></div>
      <div class="dsec">
        <div class="lab">Gesamtpreis</div>
        <div class="totrow sum"><span>Gesamtpreis</span><span>${escapeHtml(formatCents(totalCents, currency))}</span></div>
        ${tracking ? `<div class="totrow"><span>Tracking</span><span>${escapeHtml(tracking)}</span></div>` : ''}
      </div>
      <div class="dsec">
        <div class="lab">Positionen</div>
        ${positions}
      </div>
      ${takeoverSection}
      ${historySectionHtml(order, history)}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Order-Aktionen (Versand / Storno) — Modals (openModal, kl-*-Klassen)
// ---------------------------------------------------------------------------

async function triggerOrderAction(
  action: 'ship' | 'cancel',
  master: MasterRow,
  ctx: OrdersCtx,
  toast: (m: string) => void,
  onDone: () => void,
): Promise<void> {
  let units: DetailRow[] = [];
  try {
    units = await loadOrderUnits(ctx, master.storefront, master.id_order);
  } catch (e) {
    toast(`Positionen konnten nicht geladen werden: ${(e as Error).message}`);
    return;
  }
  const candidates = units.filter((u) => actionAvailableForUnit(action, u.status ?? ''));
  if (candidates.length === 0) {
    toast('Keine passende Position für diese Aktion gefunden.');
    return;
  }
  if (candidates.length > 1) {
    toast(`Diese Bestellung hat ${candidates.length} Positionen für diese Aktion, bitte Einzelposition im Detail wählen (in Kürze).`);
    return;
  }
  const unit = candidates[0]!;
  if (action === 'ship') openShipDialog(ctx, unit, onDone);
  else openCancelDialog(ctx, unit, onDone);
}

function actionAvailableForUnit(action: 'ship' | 'cancel', status: string): boolean {
  const g = statusGroup(status);
  if (action === 'ship') return g === 'processable';
  return g === 'announced' || g === 'processable';
}

// ---------------------------------------------------------------------------
// Data loaders
// ---------------------------------------------------------------------------

function mapMasterRow(r: Record<string, unknown>): MasterRow {
  return {
    kind: 'master',
    storefront: (r as { storefront?: string }).storefront ?? STOREFRONT,
    id_order: (r as { id_order: string }).id_order,
    order_number: (r as { order_number: string | null }).order_number ?? null,
    ts_created: (r as { ts_created: string | null }).ts_created ?? null,
    total_price_cents: (r as { total_price_cents: number | null }).total_price_cents ?? null,
    currency: (r as { currency: string | null }).currency ?? null,
    status_agg: 'unknown' as AggStatus,
    ship_state: null,
    units_count: Number((r as { units_count: number | null }).units_count ?? 0),
    units_mapped_count: Number((r as { units_mapped_count: number | null }).units_mapped_count ?? 0),
    customer_name: null,
    tracking: null,
    vario_order_id: null,
    vario_order_number: null,
  };
}

/** Status-Aggregation + Tracking-Fallback (aus order_units) pro Order. Setzt in-place. */
async function applyStatusAggregation(ctx: OrdersCtx, masters: MasterRow[]): Promise<void> {
  if (masters.length === 0) return;
  const ids = masters.map((m) => m.id_order);
  const { data: units, error } = await ctx.supabase
    .schema('otto')
    .from('order_units')
    .select('id_order, storefront, status, tracking_number, local_ship_state')
    .in('id_order', ids);
  if (error) {
    console.warn('[orders] status aggregation failed', error);
    return;
  }
  const byOrder = new Map<
    string,
    { statuses: Array<string | null>; tracking: string | null; total: number; shipped: number; reporting: number }
  >();
  for (const u of units ?? []) {
    const row = u as {
      id_order: string; storefront: string; status: string | null; tracking_number: string | null;
      local_ship_state: string | null;
    };
    const key = `${row.storefront}::${row.id_order}`;
    const entry = byOrder.get(key) ?? { statuses: [], tracking: null, total: 0, shipped: 0, reporting: 0 };
    entry.statuses.push(row.status);
    entry.total += 1;
    // „Versendet" = lokal gemeldet ODER Otto meldet SENT (poll-sicher, da local_ship_state Vorrang hat).
    if (row.local_ship_state === 'reported' || row.status === 'SENT') entry.shipped += 1;
    else if (row.local_ship_state === 'reporting') entry.reporting += 1;
    if (!entry.tracking && row.tracking_number) entry.tracking = row.tracking_number;
    byOrder.set(key, entry);
  }
  for (const m of masters) {
    const entry = byOrder.get(orderKey(m));
    if (!entry) continue;
    m.status_agg = aggregateStatus(entry.statuses);
    m.tracking = entry.tracking;
    // Alle Positionen versendet → 'reported' (Versendet); sonst mind. eine in Zustellung → 'reporting'.
    m.ship_state = entry.total > 0 && entry.shipped === entry.total
      ? 'reported'
      : entry.shipped > 0 || entry.reporting > 0
        ? 'reporting'
        : null;
  }
}

/** Kundenname (aus shipping_address) + Übernahme-Stand (vario_order_id/number) pro Seite — eine
 *  Query, da die View diese Felder nicht liefert. Setzt in-place. */
async function applyCustomerNames(ctx: OrdersCtx, masters: MasterRow[]): Promise<void> {
  if (masters.length === 0) return;
  const ids = masters.map((m) => m.id_order);
  const { data, error } = await ctx.supabase
    .schema('otto')
    .from('orders')
    .select('id_order, storefront, shipping_address, vario_order_id, vario_order_number')
    .in('id_order', ids);
  if (error) {
    console.warn('[orders] customer names failed', error.message);
    return;
  }
  const byKey = new Map<string, { name: string | null; varioOrderId: string | null; varioOrderNumber: string | null }>();
  for (const r of (data ?? []) as Array<{
    id_order: string; storefront: string; shipping_address: OttoAddress | null;
    vario_order_id: string | null; vario_order_number: string | null;
  }>) {
    byKey.set(`${r.storefront}::${r.id_order}`, {
      name: deriveCustomerName(r.shipping_address ?? null),
      varioOrderId: r.vario_order_id ?? null,
      varioOrderNumber: r.vario_order_number ?? null,
    });
  }
  for (const m of masters) {
    const entry = byKey.get(orderKey(m));
    if (!entry) continue;
    m.customer_name = entry.name;
    m.vario_order_id = entry.varioOrderId;
    m.vario_order_number = entry.varioOrderNumber;
  }
}

async function loadSingleMaster(ctx: OrdersCtx, storefront: string, idOrder: string): Promise<MasterRow | null> {
  const { data, error } = await ctx.supabase
    .schema('otto')
    .from('orders_with_mapping_status')
    .select('storefront,id_order,order_number,ts_created,total_price_cents,currency,units_count,units_mapped_count')
    .eq('storefront', storefront)
    .eq('id_order', idOrder)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const master = mapMasterRow(data as Record<string, unknown>);
  await applyStatusAggregation(ctx, [master]);
  await applyCustomerNames(ctx, [master]);
  return master;
}

async function loadOrdersPage(
  ctx: OrdersCtx,
  filters: FilterState,
  startRow: number,
  endRow: number,
): Promise<{ masters: MasterRow[]; total: number }> {
  let restrictedIdOrders: string[] | null = null;
  const dbStatuses = dbStatusesForFilter(filters.status);
  if (dbStatuses.length > 0) {
    const { data: unitData, error: unitErr } = await ctx.supabase
      .schema('otto')
      .from('order_units')
      .select('id_order')
      .in('status', dbStatuses as string[])
      .limit(10000);
    if (unitErr) throw new Error(unitErr.message);
    restrictedIdOrders = Array.from(new Set((unitData ?? []).map((r) => (r as { id_order: string }).id_order)));
    if (restrictedIdOrders.length === 0) return { masters: [], total: 0 };
  }

  // Sonder-Filter „Wird versendet" / „Versendet": über den lokalen, poll-sicheren local_ship_state
  // (Versendet zusätzlich über Otto-status='SENT', falls Otto den Versand selbst schon meldet).
  if (filters.status === 'shipping' || filters.status === 'sent') {
    let uq = ctx.supabase.schema('otto').from('order_units').select('id_order').limit(10000);
    uq = filters.status === 'shipping'
      ? uq.eq('local_ship_state', 'reporting')
      : uq.or('status.eq.SENT,local_ship_state.eq.reported');
    const { data: shipData, error: shipErr } = await uq;
    if (shipErr) throw new Error(shipErr.message);
    restrictedIdOrders = Array.from(new Set((shipData ?? []).map((r) => (r as { id_order: string }).id_order)));
    if (restrictedIdOrders.length === 0) return { masters: [], total: 0 };
  }

  // Sonder-Filter „Übernommen": auf Bestellungen mit gesetztem vario_order_id einschränken.
  if (filters.status === 'taken_over') {
    const { data: takenData, error: takenErr } = await ctx.supabase
      .schema('otto')
      .from('orders')
      .select('id_order')
      .not('vario_order_id', 'is', null)
      .limit(10000);
    if (takenErr) throw new Error(takenErr.message);
    restrictedIdOrders = Array.from(new Set((takenData ?? []).map((r) => (r as { id_order: string }).id_order)));
    if (restrictedIdOrders.length === 0) return { masters: [], total: 0 };
  }

  let q = ctx.supabase
    .schema('otto')
    .from('orders_with_mapping_status')
    .select(
      'storefront,id_order,order_number,ts_created,total_price_cents,currency,units_count,units_mapped_count',
      { count: 'exact' },
    );
  if (restrictedIdOrders) q = q.in('id_order', restrictedIdOrders);
  if (filters.search.trim()) {
    const term = `%${filters.search.trim().replace(/[%_]/g, (m) => `\\${m}`)}%`;
    q = q.or(`id_order.ilike.${term},order_number.ilike.${term}`);
  }
  q = q.order('ts_created', { ascending: false, nullsFirst: false });
  q = q.range(startRow, endRow - 1);

  const { data, count, error } = await q;
  if (error) throw new Error(error.message);

  const masters: MasterRow[] = (data ?? []).map((r) => mapMasterRow(r as Record<string, unknown>));
  await applyStatusAggregation(ctx, masters);
  await applyCustomerNames(ctx, masters);

  return { masters, total: count ?? masters.length };
}

async function loadOrderUnits(ctx: OrdersCtx, storefront: string, idOrder: string): Promise<DetailRow[]> {
  const { data, error } = await ctx.supabase
    .schema('otto')
    .from('order_units_with_mapping')
    .select(
      'storefront,id_order,id_order_unit,sku,ean,article_number,title,status,quantity,unit_price_cents,currency,carrier_code,tracking_number,cancellation_reason,ts_status_changed,vario_article_id,vario_article_number,channel_vario_article_id,channel_vario_listing_id',
    )
    .eq('storefront', storefront)
    .eq('id_order', idOrder)
    .order('id_order_unit', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => ({ kind: 'detail', ...(r as Omit<DetailRow, 'kind'>) }));
}

async function loadOrderHeader(ctx: OrdersCtx, storefront: string, idOrder: string): Promise<OrderHeaderFull> {
  const { data, error } = await ctx.supabase
    .schema('otto')
    .from('orders')
    .select('billing_address, shipping_address, currency, vario_order_id, vario_order_number')
    .eq('storefront', storefront)
    .eq('id_order', idOrder)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return { billing_address: null, shipping_address: null, currency: null, vario_order_id: null, vario_order_number: null };
  const row = data as {
    billing_address: OttoAddress | null; shipping_address: OttoAddress | null; currency: string | null;
    vario_order_id: string | null; vario_order_number: string | null;
  };
  return {
    billing_address: row.billing_address ?? null,
    shipping_address: row.shipping_address ?? null,
    currency: row.currency ?? null,
    vario_order_id: row.vario_order_id ?? null,
    vario_order_number: row.vario_order_number ?? null,
  };
}

/** Sind alle 4 Vario-Pflichtmodalitäten (Zahlart/-bedingung, Versandart/Lieferbedingung) in den
 *  Otto-Einstellungen gesetzt? Steuert die Aktivierung des „Übernehmen"-Buttons. Bei Lesefehler
 *  true (nicht fälschlich blocken — otto_enqueue_order_create prüft serverseitig ohnehin). */
async function loadOrderTakeoverSettingsComplete(ctx: OrdersCtx): Promise<boolean> {
  try {
    const { data, error } = await ctx.supabase.rpc('otto_list_storefronts');
    if (error) return true;
    const row = (Array.isArray(data) ? data[0] : data) as {
      vario_payment_method_id?: string | null; vario_payment_term_id?: string | null;
      vario_delivery_method_id?: string | null; vario_delivery_term_id?: string | null;
    } | null;
    if (!row) return false;
    return !!(row.vario_payment_method_id && row.vario_payment_term_id
      && row.vario_delivery_method_id && row.vario_delivery_term_id);
  } catch {
    return true;
  }
}

// ---------------------------------------------------------------------------
// Formatter helpers
// ---------------------------------------------------------------------------

function formatDateShort(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function timeShort(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatCents(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined) return '';
  const code = currency && currency.length === 3 ? currency : 'EUR';
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: code }).format(value / 100);
  } catch {
    return `${(value / 100).toFixed(2)} ${code}`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

// ---------------------------------------------------------------------------
// Modal helpers — Versand / Storno (openModal, kl-*-Klassen)
// ---------------------------------------------------------------------------

function openShipDialog(ctx: OrdersCtx, row: DetailRow, onDone: () => void): void {
  const carrierOpts = CARRIERS.map((c) => `<option value="${escapeHtml(c.value)}">${escapeHtml(c.label)}</option>`).join('');
  const retCarrierOpts = RETURN_CARRIERS.map((c) => `<option value="${escapeHtml(c.value)}">${escapeHtml(c.label)}</option>`).join('');
  const articleLabel = row.title ?? row.sku ?? row.ean ?? '—';
  const modal = openModal({
    title: 'Versand bestätigen',
    icon: 'fa-truck-fast',
    width: 540,
    body: `
      <div class="kl-modal-meta">
        <strong>${escapeHtml(row.id_order)}</strong>
        <div class="text-grey">${escapeHtml(articleLabel)}</div>
      </div>
      <div class="kl-form-row">
        <label>Versanddienstleister</label>
        <div class="v-select"><select id="m-ship-carrier">${carrierOpts}</select></div>
      </div>
      <div class="kl-form-row">
        <label>Tracking-Nummer</label>
        <div class="v-input">
          <span class="v-icon fa-light fa-barcode"></span>
          <input id="m-ship-tracking" placeholder="z. B. 003404342921…" />
        </div>
        <div class="kl-form-hint">Pflicht. Wird an OTTO gemeldet und setzt die Position auf „Versendet“.</div>
      </div>
      <div class="kl-form-row">
        <label>Retouren-Dienstleister</label>
        <div class="v-select"><select id="m-ship-ret-carrier">${retCarrierOpts}</select></div>
      </div>
      <div class="kl-form-row">
        <label>Retouren-Trackingnummer</label>
        <div class="v-input">
          <span class="v-icon fa-light fa-arrow-rotate-left"></span>
          <input id="m-ship-ret-tracking" placeholder="Nummer des beigelegten Retourenscheins" />
        </div>
        <div class="kl-form-hint">Bei Paketversand verlangt OTTO die Nummer des Retourenscheins. Bei Speditionsversand optional.</div>
      </div>
      <div class="kl-form-hint" id="m-ship-from"></div>
      <div class="kl-form-row">
        <label>Notiz (optional)</label>
        <div class="v-input"><input id="m-ship-notes" placeholder="interne Notiz, z. B. Sammelversand" /></div>
      </div>
      <div class="kl-modal-error" id="m-ship-error" hidden></div>
    `,
    footer: `
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button v-button-primary" id="m-ship-go">
        <span class="v-icon fa-light fa-check" data-icon-default></span>
        Versand bestätigen
      </button>
    `,
  });

  const carrierSel = modal.el.querySelector<HTMLSelectElement>('#m-ship-carrier')!;
  const trackingInp = modal.el.querySelector<HTMLInputElement>('#m-ship-tracking')!;
  const notesInp = modal.el.querySelector<HTMLInputElement>('#m-ship-notes')!;
  const retCarrierSel = modal.el.querySelector<HTMLSelectElement>('#m-ship-ret-carrier')!;
  const retTrackingInp = modal.el.querySelector<HTMLInputElement>('#m-ship-ret-tracking')!;
  const shipFromEl = modal.el.querySelector<HTMLDivElement>('#m-ship-from')!;
  // Retouren-Carrier folgt dem Versand-Carrier, solange der für Retouren zulässig ist.
  carrierSel.addEventListener('change', () => {
    if (RETURN_CARRIERS.some((c) => c.value === carrierSel.value)) retCarrierSel.value = carrierSel.value;
  });
  // Absenderadresse kommt aus den Einstellungen; fehlt sie, lehnt der Versand-Job ab. Vorab anzeigen.
  void (async () => {
    const { data } = await ctx.supabase.rpc('otto_get_ship_from', { _storefront: 'otto' });
    const r = (Array.isArray(data) ? data[0] : data) as { zip_code?: string | null; city?: string | null; country_code?: string | null } | null;
    shipFromEl.innerHTML = r?.zip_code && r.city && r.country_code
      ? `Absender laut Einstellungen: ${escapeHtml(r.zip_code)} ${escapeHtml(r.city)}, ${escapeHtml(r.country_code)}`
      : '<b>Absenderadresse fehlt.</b> Bitte zuerst in den Einstellungen unter „Versand“ PLZ, Ort und Land eintragen, sonst lehnt OTTO den Versand ab.';
  })();
  const errEl = modal.el.querySelector<HTMLDivElement>('#m-ship-error')!;
  const submitBtn = modal.el.querySelector<HTMLButtonElement>('#m-ship-go')!;
  const submitIcon = submitBtn.querySelector<HTMLElement>('[data-icon-default]');

  trackingInp.focus();
  submitBtn.addEventListener('click', async () => {
    const carrier = carrierSel.value.trim();
    const tracking = trackingInp.value.trim();
    errEl.hidden = true;
    if (!carrier) { errEl.textContent = 'Versanddienstleister wählen.'; errEl.hidden = false; return; }
    if (!tracking) { errEl.textContent = 'Tracking-Nummer ist erforderlich.'; errEl.hidden = false; return; }
    const retTracking = retTrackingInp.value.trim();
    if (!retTracking && carrier !== 'OTHER_FORWARDER') {
      errEl.textContent = 'Retouren-Trackingnummer ist bei Paketversand erforderlich.'; errEl.hidden = false; return;
    }
    submitBtn.disabled = true;
    if (submitIcon) submitIcon.className = 'v-icon fa-light fa-spinner-third fa-spin';
    try {
      const { data, error } = await ctx.supabase.rpc('otto_enqueue_order_unit_ship', {
        _id_order_unit: row.id_order_unit,
        _carrier_code: carrier,
        _tracking_number: tracking,
        _notes: notesInp.value.trim() || null,
        _return_carrier: retTracking ? retCarrierSel.value : null,
        _return_tracking_number: retTracking || null,
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
      if (jobId) await awaitJob(ctx.supabase, jobId, 60_000);
      modal.close();
      onDone();
    } catch (e) {
      errEl.textContent = `Fehler: ${(e as Error).message}`;
      errEl.hidden = false;
      submitBtn.disabled = false;
      if (submitIcon) submitIcon.className = 'v-icon fa-light fa-check';
    }
  });
}

function openCancelDialog(ctx: OrdersCtx, row: DetailRow, onDone: () => void): void {
  const articleLabel = row.title ?? row.sku ?? row.ean ?? '—';
  const modal = openModal({
    title: 'Position stornieren',
    icon: 'fa-ban',
    width: 540,
    body: `
      <div class="kl-modal-meta">
        <strong>${escapeHtml(row.id_order)}</strong>
        <div class="text-grey">${escapeHtml(articleLabel)}</div>
      </div>
      <div class="kl-form-row">
        <label>Storno-Grund (optional)</label>
        <div class="v-input"><input id="m-cancel-reason" placeholder="z. B. Kein Bestand" /></div>
        <div class="kl-form-hint">Storniert die Position bei Otto (nur aus „Angekündigt"/„Zu versenden" möglich).</div>
      </div>
      <div class="v-banner v-banner-warning">
        <span class="v-icon fa-light fa-triangle-exclamation"></span>
        <div>Stornos schlagen auf deine Otto-Verkäufer-Performance. Nur einsetzen, wenn die Position wirklich nicht erfüllt werden kann.</div>
      </div>
      <div class="kl-modal-error" id="m-cancel-error" hidden></div>
    `,
    footer: `
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button kl-cancel-confirm-btn" id="m-cancel-go">
        <span class="v-icon fa-light fa-ban" data-icon-default></span>
        Stornieren
      </button>
    `,
  });

  const reasonInp = modal.el.querySelector<HTMLInputElement>('#m-cancel-reason')!;
  const errEl = modal.el.querySelector<HTMLDivElement>('#m-cancel-error')!;
  const submitBtn = modal.el.querySelector<HTMLButtonElement>('#m-cancel-go')!;
  const submitIcon = submitBtn.querySelector<HTMLElement>('[data-icon-default]');

  submitBtn.addEventListener('click', async () => {
    errEl.hidden = true;
    submitBtn.disabled = true;
    if (submitIcon) submitIcon.className = 'v-icon fa-light fa-spinner-third fa-spin';
    try {
      const { data, error } = await ctx.supabase.rpc('otto_enqueue_order_unit_cancel', {
        _id_order_unit: row.id_order_unit,
        _reason: reasonInp.value.trim() || null,
      });
      if (error) throw new Error(error.message);
      const jobId = Array.isArray(data) ? (data[0]?.job_id as string) : (data as { job_id?: string } | null)?.job_id;
      if (jobId) await awaitJob(ctx.supabase, jobId, 60_000);
      modal.close();
      onDone();
    } catch (e) {
      errEl.textContent = `Fehler: ${(e as Error).message}`;
      errEl.hidden = false;
      submitBtn.disabled = false;
      if (submitIcon) submitIcon.className = 'v-icon fa-light fa-ban';
    }
  });
}
